/** In-memory contact detail payloads for seamless prev/next without loading UI. */

import { CommandPage } from "@/lib/command"
import { contactRecordFromCommandData } from "@/lib/contact-command-data"

const CONTACT_COMMAND_PATH = "/app/crm/base/cmd/contact"

const contactById = new Map<string, Record<string, unknown>>()
let holdover: { id: string; data: Record<string, unknown> } | null = null
const inflight = new Map<string, Promise<Record<string, unknown> | null>>()

export function getCachedContactDetail(contactId: string): Record<string, unknown> | null {
  const id = contactId.trim()
  if (!id) return null
  return contactById.get(id) ?? null
}

/** Last successfully displayed contact — used when remounting for a neighbor before its payload arrives. */
export function getContactDetailHoldover(): { id: string; data: Record<string, unknown> } | null {
  return holdover
}

export function rememberContactDetail(
  contactId: string,
  data: Record<string, unknown>,
): void {
  const id = contactId.trim()
  if (!id) return
  contactById.set(id, data)
  holdover = { id, data }
}

/** Drop cached detail so the next load hits the server (e.g. after pipeline stage moves). */
export function invalidateContactDetailCache(contactId?: string): void {
  if (contactId == null || !String(contactId).trim()) {
    contactById.clear()
    holdover = null
    inflight.clear()
    return
  }
  const id = String(contactId).trim()
  contactById.delete(id)
  if (holdover?.id === id) holdover = null
  inflight.delete(id)
}

export function seedContactDetailInitial(
  contactId: string,
): {
  contactData: Record<string, unknown> | null
  displayedContactId: string
  loading: boolean
} {
  const id = contactId.trim()
  const cached = id ? contactById.get(id) : undefined
  if (cached) {
    return { contactData: cached, displayedContactId: id, loading: false }
  }
  if (holdover) {
    return {
      contactData: holdover.data,
      displayedContactId: holdover.id,
      loading: false,
    }
  }
  return { contactData: null, displayedContactId: id, loading: Boolean(id) }
}

export async function fetchContactDetailCached(
  contactId: string,
  options?: { force?: boolean },
): Promise<Record<string, unknown> | null> {
  const id = contactId.trim()
  if (!id) return null
  if (options?.force) {
    contactById.delete(id)
    if (holdover?.id === id) holdover = null
    inflight.delete(id)
  } else {
    const cached = contactById.get(id)
    if (cached) return cached
    const pending = inflight.get(id)
    if (pending) return pending
  }

  const promise = (async () => {
    try {
      const cmd = CommandPage(CONTACT_COMMAND_PATH)
      const res = await cmd.load({ contact_id: id })
      if (!res.ok || !res.data) return null
      const next = contactRecordFromCommandData(res.data)
      if (!next) return null
      contactById.set(id, next)
      return next
    } finally {
      inflight.delete(id)
    }
  })()

  inflight.set(id, promise)
  return promise
}

export function prefetchContactDetail(contactId: string): void {
  const id = contactId.trim()
  if (!id || contactById.has(id) || inflight.has(id)) return
  void fetchContactDetailCached(id).catch(() => {})
}

export function prefetchAdjacentContactDetails(previous?: string | null, next?: string | null): void {
  const prev = (previous ?? "").trim()
  const nxt = (next ?? "").trim()
  if (prev) prefetchContactDetail(prev)
  if (nxt) prefetchContactDetail(nxt)
}
